// components/use-case-list/use-case-card.tsx
"use client";

import { motion } from 'framer-motion';
import { ArrowUpRight, BarChart, Bot, BriefcaseBusiness, Building2, Clock, Cpu,Database, GraduationCap, HardHat, Home, Paintbrush, Scale, ShoppingBag, Stethoscope, Wallet } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import type { JSX } from "react";

import { Badge } from "@/components/ui/badge";

interface UseCaseCardProps {
  id: string;
  title: string;
  description: string;
  categories: string[];
  models: string[];
  readTime: string | null;
  image: string | null;
}

export function UseCaseCard({
  id,
  title,
  description,
  categories = [],
  models = [],
  readTime,
  image,
}: UseCaseCardProps) {
  // Get icon for a category
  const getCategoryIcon = (category: string) => {
    const iconMap: {[key: string]: JSX.Element} = {
      "Architecture": <Building2 className="w-4 h-4 mr-1" />,
      "Real Estate": <Home className="w-4 h-4 mr-1" />,
      "Construction": <HardHat className="w-4 h-4 mr-1" />,
      "Design": <Paintbrush className="w-4 h-4 mr-1" />,
      "Retail": <ShoppingBag className="w-4 h-4 mr-1" />,
      "Healthcare": <Stethoscope className="w-4 h-4 mr-1" />,
      "Marketing": <BarChart className="w-4 h-4 mr-1" />,
      "Finance": <Wallet className="w-4 h-4 mr-1" />,
      "Legal": <Scale className="w-4 h-4 mr-1" />,
      "Education": <GraduationCap className="w-4 h-4 mr-1" />,
      "Business": <BriefcaseBusiness className="w-4 h-4 mr-1" />,
    };
    
    return iconMap[category] || <Building2 className="w-4 h-4 mr-1" />;
  };

  // Get icon for a model
  const getModelIcon = (model: string) => {
    const iconMap: {[key: string]: JSX.Element} = {
      "Gemini": <Cpu className="w-3 h-3 mr-1" />,
      "Claude": <Bot className="w-3 h-3 mr-1" />,
      "GPT-4": <Bot className="w-3 h-3 mr-1" />,
      "Midjourney": <Paintbrush className="w-3 h-3 mr-1" />,
      "Vector DB": <Database className="w-3 h-3 mr-1" />,
      "LLaMA": <Bot className="w-3 h-3 mr-1" />,
      "Stable Diffusion": <Paintbrush className="w-3 h-3 mr-1" />,
    };
    
    return iconMap[model] || <Cpu className="w-3 h-3 mr-1" />;
  };

  // Use a single accent color for all cards to maintain consistency
  const accentColor = "bg-blue-600";

  return (
    <Link href={`/ai-use-cases/${id}`} className="block h-full">
      <motion.div 
        className="relative h-full overflow-hidden bg-gray-800/50 rounded-lg shadow-lg border-b border-r border-l border-gray-700 flex flex-col"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.4 } }}
        whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
      >
        {/* Top accent border */}
        <div className={`absolute top-0 left-0 right-0 h-2 ${accentColor}`} />
        
        {/* Image container */}
        <div className="relative w-full h-48 overflow-hidden">
          <Image
            src={image ? (!image.startsWith('http') ? image : '/placeholder.svg?height=400&width=600') : '/placeholder.svg?height=400&width=600'}
            alt={title || 'Use Case'}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent opacity-80" />

          {/* Read time */}
          <div className="absolute top-3 right-3">
            <div className="flex items-center bg-black/60 text-white rounded px-2 py-1 text-xs">
              <Clock className="w-3 h-3 mr-1" />
              {readTime || '5 min'}
            </div>
          </div>
        </div>

        {/* Content section */}
        <div className="flex flex-col flex-grow p-5 pt-4">
          {/* Categories with icons */}
          <div className="flex flex-wrap gap-2 mb-3">
            {(categories || []).map((category) => (
              <Badge
                key={category}
                className="bg-gray-900/70 hover:bg-gray-900 text-white text-xs font-medium px-2 py-1 flex items-center"
              >
                {getCategoryIcon(category)}
                {category}
              </Badge>
            ))}
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
            {title || 'Untitled Use Case'}
          </h3>

          {/* Description */}
          <p className="text-gray-300 text-sm mb-4 line-clamp-3">
            {description || 'No description available'}
          </p>

          {/* Model badges with icons */}
          <div className="flex flex-wrap gap-2 mt-auto mb-4">
            {(models || []).map((model) => (
              <Badge
                key={model}
                variant="outline"
                className="border-gray-700 bg-gray-900/80 text-gray-300 text-xs font-normal flex items-center"
              >
                {getModelIcon(model)}
                {model}
              </Badge>
            ))}
          </div>

          {/* Call to action */}
          <div className="mt-auto pt-3 border-t border-gray-700 flex justify-between items-center">
            <span className="text-white text-sm font-medium group-hover:text-blue-400">
              Read case study
            </span>
            <ArrowUpRight className="w-4 h-4 text-blue-400 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </div>
        </div>
      </motion.div>
    </Link>
  );
} 